import type { Category, PlanningCycle, PublishedView, SpaceTemplateBundle, TaskTemplate, Todo } from "../domain/entities.ts";
import type { SavedView, TaskSyncStatus } from "../domain/app-types.ts";
import type { OutboxChange, StoredEntity } from "../infrastructure/storage/indexed-db.ts";
import type { SyncState } from "../infrastructure/sync/automerge.ts";

export interface ShellDataCapabilities {
  crossSpace?(): boolean;
  rootMatches?(rootId: string): boolean;
  entities(): Promise<StoredEntity[]>;
  outbox(): Promise<OutboxChange[]>;
  visible(entity: StoredEntity): boolean;
  activeRoot(): string | null;
  myDay(): boolean;
  personalSavedViews(): SavedView[];
  conflictingFields(entity: StoredEntity): string[];
  syncState(): SyncState;
}

export interface ShellData {
  epics?: import("../domain/entities.ts").Epic[];
  allAvailableTodos: Todo[];
  todos: Todo[];
  conflicts: Map<string, string[]>;
  sharedTemplates: TaskTemplate[];
  spaceTemplateBundles: SpaceTemplateBundle[];
  savedViews: SavedView[];
  cycles: PlanningCycle[];
  activeCycle: string | null;
  categories: Category[];
  indexedCategories: Category[];
  allCategories: Category[];
  taskSyncStatuses: Map<string, TaskSyncStatus>;
}

export async function loadShellData(capabilities: ShellDataCapabilities): Promise<ShellData> {
  const allEntities = await capabilities.entities();
  const scopedEntities = allEntities.filter(capabilities.visible);
  const activeRoot = capabilities.activeRoot();
  const rootEntities = activeRoot ? scopedEntities.filter(entity => entity.rootId === activeRoot) : [];
  const entities = capabilities.crossSpace?.() ? scopedEntities.filter(entity => capabilities.rootMatches?.(entity.rootId) ?? true) : capabilities.myDay() ? scopedEntities : rootEntities;
  const conflicts = new Map(entities.map(entity => [entity.id, capabilities.conflictingFields(entity)]));
  const todos = entities.filter(entity => entity.kind === "todo").map(entity => entity.projection as Todo);
  const sharedTemplates = rootEntities.filter(entity => entity.kind === "template").map(entity => entity.projection as TaskTemplate).filter(template => !template.deleted);
  const spaceTemplateBundles = rootEntities.filter(entity => entity.kind === "space_template").map(entity => entity.projection as SpaceTemplateBundle).filter(template => !template.deleted);
  const publishedViews = rootEntities.filter(entity => entity.kind === "saved_view").map(entity => entity.projection as PublishedView).filter(view => !view.deleted).map(view => ({ ...view, published: true } satisfies SavedView));
  const cycles = rootEntities.filter(entity => entity.kind === "cycle").map(entity => entity.projection as PlanningCycle).filter(cycle => !cycle.deleted);
  const categories = rootEntities.filter(entity => entity.kind === "category").map(entity => entity.projection as Category).filter(category => !category.deleted && !category.archivedAt);
  const queuedIds = new Set((await capabilities.outbox()).map(change => change.entityId));
  const syncState = capabilities.syncState();
  const taskSyncStatuses = new Map(todos.map(todo => {
    const status: TaskSyncStatus = conflicts.get(todo.id)?.length ? "conflicted" : queuedIds.has(todo.id) ? syncState === "attention" ? "error" : "queued" : syncState === "synced" ? "synced" : "local";
    return [todo.id, status];
  }));
  const indexedCategories = allEntities.filter(entity => entity.kind === "category" && capabilities.visible(entity)).map(entity => entity.projection as Category).filter(category => !category.deleted);
  return {
    allAvailableTodos: scopedEntities.filter(entity => entity.kind === "todo").map(entity => entity.projection as Todo),
    todos,
    epics: entities.filter(entity => entity.kind === "epic").map(entity => entity.projection as import("../domain/entities.ts").Epic).filter(epic => !epic.deleted),
    conflicts,
    sharedTemplates,
    spaceTemplateBundles,
    savedViews: [...capabilities.personalSavedViews(), ...publishedViews],
    cycles,
    activeCycle: cycles.find(cycle => cycle.status === "active")?.id || null,
    categories,
    indexedCategories,
    allCategories: indexedCategories.filter(category => !category.archivedAt),
    taskSyncStatuses,
  };
}
